import { cn } from 'lib/utils'
import { CheckIcon, XIcon } from 'lucide-react'
import * as React from 'react'
import { Input } from './Input'
import { Label } from './Label'

type InlineEditProps = {
  children?: React.ReactNode
  classPrefix?: string
  className?: string
  disabled?: boolean
  defaultValue?: string
  value?: string
  showControls?: boolean
  placeholder?: string
  size?: 'lg' | 'md' | 'sm' | 'xs'
  stateOnBlur?: 'save' | 'cancel'
  labelClassName?: string
  label?: string
  inputClassName?: string
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void
  onCancel?: (value: string) => void
  onSave?: (value: string) => void
  onEdit?: () => void
}

const InlineEdit = React.forwardRef<HTMLDivElement, InlineEditProps>(
  (
    {
      children,
      classPrefix = 'inline-edit',
      className,
      disabled = false,
      defaultValue = '',
      value,
      showControls = false,
      placeholder = 'Click to edit',
      size = 'md',
      stateOnBlur = 'save',
      labelClassName,
      label,
      inputClassName,
      onChange,
      onCancel,
      onSave,
      onEdit,
      ...props
    },
    ref
  ) => {
    // Controlled vs uncontrolled handling
    const isControlled = value !== undefined
    const [isEditing, setIsEditing] = React.useState(false)
    const [inputValue, setInputValue] = React.useState(isControlled ? value : defaultValue)
    const [originalValue, setOriginalValue] = React.useState(isControlled ? value : defaultValue)

    React.useEffect(() => {
      setInputValue(defaultValue)
      setOriginalValue(defaultValue)
    }, [defaultValue])

    // Update local state when controlled value changes
    React.useEffect(() => {
      if (isControlled) {
        setInputValue(value)
        setOriginalValue(value)
      }
    }, [value, isControlled])

    const handleEdit = () => {
      if (disabled) return

      setIsEditing(true)
      onEdit?.()
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setInputValue(newValue)
      onChange?.(newValue, e)
    }

    const handleSave = () => {
      setIsEditing(false)
      onSave?.(inputValue || '')
    }

    const handleCancel = () => {
      setIsEditing(false)
      setInputValue(originalValue)
      onCancel?.(originalValue || '')
    }

    const handleBlur = () => {
      if (!showControls) {
        if (stateOnBlur === 'save') {
          handleSave()
        } else {
          handleCancel()
        }
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSave()
      } else if (e.key === 'Escape') {
        handleCancel()
      }
    }

    // Apply size classes based on the size prop
    const sizeClasses = {
      lg: 'text-lg',
      md: 'text-base',
      sm: 'text-sm',
      xs: 'text-xs',
    }

    return (
      <div
        ref={ref}
        className={cn(
          classPrefix,
          `${classPrefix}--${size}`,
          disabled && `${classPrefix}--disabled`,
          className
        )}
        {...props}
      >
        {label && <Label className={cn('mb-2', labelClassName)}>{label}</Label>}

        <div className="relative">
          {isEditing ? (
            <div className="flex items-center">
              <Input
                value={inputValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                className={cn(sizeClasses[size], inputClassName)}
                autoFocus
              />

              {showControls && (
                <div className="ml-2 flex items-center space-x-1">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="rounded-md bg-green-100 p-1 text-green-700 hover:bg-green-200"
                    aria-label="Save"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="rounded-md bg-red-100 p-1 text-red-700 hover:bg-red-200"
                    aria-label="Cancel"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div
              onClick={handleEdit}
              className={cn(
                'inline-block min-h-9 cursor-pointer rounded-md px-3 py-1 hover:bg-gray-100 dark:hover:bg-gray-800',
                sizeClasses[size],
                disabled &&
                  'cursor-not-allowed opacity-50 hover:bg-transparent dark:hover:bg-transparent'
              )}
            >
              {inputValue || children || placeholder}
            </div>
          )}
        </div>
      </div>
    )
  }
)

InlineEdit.displayName = 'InlineEdit'

export { InlineEdit }
